import type { ElementEditorProps } from '@tunasong/editor'
import { ElementEditor, createTunaEditor } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import { deepEqual, logger, shortUuid } from '@tunasong/models'
import type { TunaEditor, TunaPlugin } from '@tunasong/plugin-lib'
import { maturityFilter, useYDocConnect } from '@tunasong/plugin-lib'
import type { RootState } from '@tunasong/redux'
import { useThunkDispatch, useCurrentUser } from '@tunasong/redux'
import { useGlobalAppContext } from '@tunasong/plugin-lib'
import type { CoreElement, Entity, Persisted } from '@tunasong/schemas'
import { isCoreElement, isPersisted } from '@tunasong/schemas'
import type { Awareness, SyncProvider, Y, YjsEditor } from '@tunasong/sync-lib'
import { getElementValue } from '@tunasong/sync-lib'
import { useDelayedInvoke, useUserConfig } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useStore } from 'react-redux'
import invariant from 'tiny-invariant'
import { useYjsPlugin } from '../yjs-plugin.js'

export interface CollabEditorProps extends Omit<ElementEditorProps, 'editor' | 'element'> {
  /** We want the CollabEditor to not deal with Entities, but some plugins depend on the entityId */
  element: Entity
  doc: Y.Doc
  provider?: SyncProvider
  awareness: Awareness
  color: string
  name: string
  plugins: TunaPlugin[]
  onEditor?(editor: TunaEditor): void
}

export const CollabEditor: FC<CollabEditorProps> = ({
  /** There may be plugins that need the entityId */
  element: initialEntity,
  color,
  doc,
  provider,
  awareness,
  plugins,
  onEditor,
  ...restProps
}) => {
  const { userId } = useCurrentUser()

  invariant(isPersisted(initialEntity), 'CollabEditor requires a persisted entity')

  const [maturity] = useUserConfig('featureMaturity')

  const [entity, setEntity] = useState<Persisted<CoreElement>>()

  useEffect(() => {
    const elValue = getElementValue(doc)
    setEntity(entity => ({
      /** We need features on the top-level entity as well */
      ...initialEntity,
      children: isCoreElement(entity) ? entity.children : elValue,
    }))
  }, [initialEntity, doc])

  const { plugin: yjsPlugin } = useYjsPlugin({
    doc,
    userId,
    color,
    provider,
    awareness,
  })

  const allPlugins = useMemo(() => {
    logger.debug(`CollabEditor plugins re-created`, { plugins, yjsPlugin })
    return [...(plugins ?? []), yjsPlugin].filter(Boolean).filter(maturityFilter(maturity))
  }, [maturity, plugins, yjsPlugin])

  /**
   * @note
   * we don't use useTunaEditor() here because we need to a) create the editor once, and b) pass the plugins when yjsPlugin is updated
   * plugins must change for decorate() to be called properly, and we want useCreateEditor() to re-create when plugins change.
   */

  const dispatch = useThunkDispatch()
  const { getState } = useStore<RootState>()
  const { graph } = useGlobalAppContext()
  const id = useMemo(() => shortUuid(), [])

  const editor = useMemo<TunaEditor & YjsEditor>(
    () => createTunaEditor<TunaEditor & YjsEditor>(id, allPlugins, { dispatch, getState, graph }, maturity),
    [allPlugins, dispatch, getState, graph, id, maturity]
  )

  /**
   * @todo temporary hack to support changing awareness after the editor is created
   */
  useEffect(() => {
    /**
     * slate-yjs uses awarenmess on the editor, so we do this hack to support updating awareness
     *  @see https://github.com/BitPhinix/slate-yjs/blob/69f3e0eaecc0b81049dc57e70403df60e7a10163/packages/core/src/plugins/withCursors.ts#L175
     * Longer term we want to use the Plate options store for this, or extend
     */
    if (!editor) {
      return
    }
    editor.setAwareness(awareness)
  }, [awareness, editor])

  useEffect(() => {
    if (onEditor && editor) {
      onEditor(editor)
    }
  }, [editor, onEditor])

  /** Update metadata on the entity */
  const updateEntity = graphHooks.useEntityUpdate({ debounceDelay: 3 * 1000 })
  const delayedMetadataUpdate = useDelayedInvoke(30000, 'timeout')
  const prevMetadata = useRef(entity?.metadata)
  const handleChange = useCallback(() => {
    delayedMetadataUpdate(() => {
      if (!editor) {
        return
      }
      invariant(entity?.id, 'CollabEditor requires a persisted entity')

      const pMeta = prevMetadata.current ?? entity?.metadata
      const metadata = editor.getMetadata()
      prevMetadata.current = metadata

      // Check if the metadata has changed
      if (deepEqual(metadata, pMeta)) {
        logger.debug('Metadata has not changed, skipping update', { metadata, pMeta })
        return
      }
      updateEntity(entity.id, { metadata })
    })
  }, [delayedMetadataUpdate, editor, entity?.id, entity?.metadata, updateEntity])

  /** Connect the editor to the YDoc */
  useYDocConnect(editor)

  if (!(entity && editor)) {
    return null
  }

  invariant(isCoreElement(entity), 'CollabEditor requires a core element')

  return <ElementEditor key={entity.id} editor={editor} element={entity} onChange={handleChange} {...restProps} />
}

export default CollabEditor
